/**
 * Scheduling an event from a place's page (see `schedule-event.ts`): the
 * form's games and presets, read when it first opens (once per visit; again
 * after a failed read), the create, and its undo. Each of the two writes is
 * followed by `onChanged` (the page reads Next up again). One create at a
 * time: a second while one is in flight gets the first's answer. When the
 * create's answer is lost (it may have made the event; `lost-answer.ts`), that
 * form offers no Create again and "Schedule an event" waits while Next up is
 * read again (a read started after the loss); when the rows shown at the press
 * were known, a new row matching what was sent (title and start) is taken as
 * the event made, with its Undo. Otherwise the line points to the admin console.
 *
 * Changing or cancelling an event coming up (unit 6c, `change-event.ts`):
 * the event is read by id each time its Change or Cancel opens (only the
 * latest read lands); the change and the cancel each go one at a time, a lost
 * answer locks that form (no second send) and reads Next up again; each has
 * an undo for five minutes. The latest write here owns the one Undo: a change
 * or cancel drops a create's, and a create drops theirs.
 *
 * `?fixtures=1` serves the samples and makes nothing anywhere: no network.
 *
 * Only ever used for staff on a dev or preview build: the page shows the
 * button only when `canScheduleEvents` holds, and the calls go through the
 * staff door.
 */
import * as React from "react";

import { readGameCatalogOnce } from "@/data/place-games-api";
import {
  cancelEvent,
  createEvent,
  fetchEventToChange,
  updateEvent,
  type CreateEventInput,
  type UpdateEventInput,
} from "@/data/schedule-event-api";
import { fetchPresets } from "@/data/presets-api";
import { errorMessage } from "@/lib/backend-error";
import { usePortal } from "@/features/portal/portal-context";
import { fixtureScheduleWriteApi, sampleScheduleOptions } from "@/features/places/fixtures";
import {
  canUndo,
  findMadeRow,
  makeEvent,
  LostAnswerError,
  readScheduleOptions,
  undoEvent,
  type MadeEvent,
  type ScheduleOptions,
  type ScheduleWriteApi,
} from "@/features/places/schedule-event";
import {
  canUndoChange,
  cancelComingEvent,
  remakeLostLine,
  changeEvent,
  undoChangedEvent,
  type ChangedEvent,
  type EventSnapshot,
} from "@/features/places/change-event";
import { isLostAnswer } from "@/features/writes/lost-answer";
import { CHECKS_AFTER_MS, REREADS_AFTER_LOSS_MS, failureLine, singleFlight } from "@/features/writes/writes";
import { useOneWrite, useTimers } from "@/features/writes/use-writes";
import type { NextUp, PlaceSchedule } from "@/features/places/schedule";
import { useFixtureMode, type Load } from "@/features/places/use-places";

/** What the page hands the Schedule section to schedule an event with. */
export interface Scheduling {
  /** The form's games and presets: null until the form first opens. */
  options: Load<ScheduleOptions> | null;
  /** Ask for the games and presets (the form opened): once per visit, again after a failed read. */
  askOptions: () => void;
  /** The signed-in user's username ("" when none: the admin console's host stands in). */
  username: string;
  /** The event just made here, while its undo matters; null before any. */
  made: MadeEvent | null;
  /** Make the event. Rejects with an Error whose message is the plain line to
   *  show. While one is in flight, a second call gets the same answer. */
  create: (input: CreateEventInput) => Promise<void>;
  /** The last create's answer was lost and Next up is being read again to see
   *  whether it was made: "Schedule an event" waits until this is false. */
  checking: boolean;
  /** Cancel the event just made (within five minutes of making it). */
  undo: () => void;
  /** The event read for a row's Change or Cancel (null: none asked for here),
   *  and when it was asked for (what it is checked against: started, cancelled). */
  eventRead: { eventId: string; askedAt: number; load: Load<EventSnapshot | null> } | null;
  /** Read an event by id (a row's Change or Cancel opened): each call reads it again. */
  askEvent: (eventId: string) => void;
  /** Change it: `input` as `buildUpdateEventInput` makes it from `before`.
   *  Rejects with an Error whose message is the plain line to show (a
   *  `LostAnswerError` when it may have gone through). One at a time. */
  change: (before: EventSnapshot, input: UpdateEventInput) => Promise<void>;
  /** Cancel it; rejects as {@link change} does. One at a time. */
  cancel: (before: EventSnapshot) => Promise<void>;
  /** The event changed or cancelled here, while its undo matters; null before any. */
  changed: ChangedEvent | null;
  /** Undo that change or cancel (within five minutes). */
  undoChange: () => void;
}

/** A newer write takes over the one Undo, but never from an undo still going
 *  (its answer, a lost one included, must still land). */
function keepIfUndoing<T extends { event: { undo: unknown } }>(prev: T | null): T | null {
  return prev?.event.undo === "undoing" ? prev : null;
}

export function useScheduleEvent(
  placeId: string,
  onChanged: () => Promise<NextUp | null>,
  schedule: Load<PlaceSchedule | null>,
): Scheduling {
  const { isSuperAdmin, user } = usePortal();
  const sampleMode = useFixtureMode();

  const [options, setOptions] = React.useState<{ placeId: string; load: Load<ScheduleOptions> } | null>(null);
  const mine = options?.placeId === placeId ? options.load : null;
  // Read through a ref, so `askOptions` keeps its identity as the read goes
  // (the form asks once each time it opens, never again on its own).
  const current = React.useRef(mine);
  current.current = mine;
  // The latest options read asked for: only its answer lands.
  const latest = React.useRef(0);
  const askOptions = React.useCallback(() => {
    if (current.current && current.current.state !== "error") return;
    if (sampleMode) {
      const load: Load<ScheduleOptions> = { state: "ready", data: sampleScheduleOptions(), sample: true, readAt: null, refreshFailed: false };
      setOptions({ placeId, load });
      return;
    }
    setOptions({ placeId, load: { state: "loading" } });
    const viewer = { isSuperAdmin };
    // Only the latest read's answer lands: a late one (for a place left
    // behind, or an earlier try here) never overwrites a newer read.
    const request = ++latest.current;
    const land = (load: Load<ScheduleOptions>) => {
      if (latest.current === request) setOptions({ placeId, load });
    };
    readScheduleOptions({ catalog: () => readGameCatalogOnce(viewer), presets: () => fetchPresets(viewer) }).then(
      (data) => land({ state: "ready", data, sample: false, readAt: Date.now(), refreshFailed: false }),
      (e: unknown) => {
        console.warn("Could not read the games and rules for scheduling:", e);
        land({ state: "error", message: errorMessage(e, "Could not read the games and rules.") });
      },
    );
  }, [placeId, sampleMode, isSuperAdmin]);

  const api: ScheduleWriteApi = React.useMemo(() => {
    if (sampleMode) return fixtureScheduleWriteApi();
    const viewer = { isSuperAdmin };
    return {
      create: (input) => createEvent(input, viewer),
      cancel: (id) => cancelEvent(id, viewer),
      event: (id) => fetchEventToChange(id, viewer),
      update: (input) => updateEvent(input, viewer),
    };
  }, [sampleMode, isSuperAdmin]);

  // The event just made, per place: moving to another place forgets it.
  const [made, setMade] = React.useState<{ placeId: string; event: MadeEvent } | null>(null);
  const madeHere = made?.placeId === placeId ? made.event : null;
  // The event changed or cancelled here, per place, likewise.
  const [changed, setChanged] = React.useState<{ placeId: string; event: ChangedEvent } | null>(null);
  const changedHere = changed?.placeId === placeId ? changed.event : null;

  // Next up's rows as last shown (null while not read), for what a lost
  // create is checked against; kept after each commit, never mid-render.
  const rows = schedule.state === "ready" && schedule.data ? schedule.data.nextUp.rows : null;
  const rowsNow = React.useRef(rows);
  React.useEffect(() => {
    rowsNow.current = rows;
  });

  // A create whose answer was lost, while Next up is read again to see.
  const [checking, setChecking] = React.useState<{ placeId: string; at: number } | null>(null);
  // The place on screen (a check for a place left behind never lands here),
  // and the checks' timers (cleared when the page goes).
  const placeNow = React.useRef(placeId);
  React.useEffect(() => {
    placeNow.current = placeId;
  });
  const after = useTimers();

  // One write at a time on this page, of any kind (a create, a change, a
  // cancel, an undo): another while one is going is refused, so no answer
  // ever lands on a record a newer write replaced (a double press of the same
  // button is `singleFlight`'s: it gets the first's answer).
  const writes = useOneWrite();
  const oneWrite = writes.run;

  const create = React.useMemo(
    () =>
      singleFlight((input: CreateEventInput) => oneWrite(async () => {
        const shown = rowsNow.current;
        const at = Date.now();
        let event: MadeEvent;
        try {
          event = await makeEvent(input, api, at);
        } catch (e) {
          console.warn("Could not make the event:", e);
          if (!isLostAnswer(e)) throw new Error(failureLine(e, "make it"));
          // With the rows before unknown, nothing can be matched: Next up is
          // read again once so it is current, and the line points to the admin console.
          if (!shown) {
            void onChanged();
            throw new LostAnswerError(false);
          }
          // Read Next up again at each of CHECKS_AFTER_MS (reads started after
          // the loss): a new row with the title and start sent is the event.
          // The button waits until it is found or the last check is done; a
          // find lands only on the place it was made for.
          const before = new Set(shown.map((r) => r.id));
          let settled = false;
          const settle = () => {
            settled = true;
            setChecking((c) => (c?.at === at ? null : c));
          };
          setChecking({ placeId, at });
          CHECKS_AFTER_MS.forEach((delay, i) => {
            after(delay, () => {
              if (settled) return;
              void onChanged().then((nextUp) => {
                if (settled) return;
                const row = nextUp ? findMadeRow(nextUp.rows, before, input) : undefined;
                if (row && placeNow.current === placeId) {
                  setChanged(keepIfUndoing);
                  setMade({ placeId, event: { id: row.id, title: row.title, startAt: row.startAt, seasonId: input.seasonId ?? null, madeAt: at, undo: "ready" } });
                  settle();
                } else if (i === CHECKS_AFTER_MS.length - 1) settle();
              });
            });
          });
          throw new LostAnswerError(true);
        }
        setChanged(keepIfUndoing);
        setMade({ placeId, event });
        void onChanged();
      })),
    [api, placeId, onChanged, oneWrite, after],
  );

  const undo = React.useCallback(() => {
    // Checked again at the click, whatever is still on screen: never past the
    // five minutes, never while one is going or once it is undone.
    const now = Date.now();
    if (!madeHere || !canUndo(madeHere, now) || writes.busy) return;
    const set = (undoState: MadeEvent["undo"]) =>
      setMade((prev) => (prev?.event.id === madeHere.id ? { ...prev, event: { ...prev.event, undo: undoState } } : prev));
    set("undoing");
    oneWrite(() => undoEvent(madeHere, api, now)).then(
      (how) => {
        set(how);
        void onChanged();
      },
      (e: unknown) => {
        console.warn("Could not undo the event:", e);
        set({ failed: failureLine(e, "undo it") });
      },
    );
  }, [madeHere, api, onChanged, oneWrite, writes]);

  // The event read for a row's Change or Cancel: only the latest read lands.
  const [eventRead, setEventRead] = React.useState<{
    placeId: string;
    eventId: string;
    askedAt: number;
    load: Load<EventSnapshot | null>;
  } | null>(null);
  const latestEvent = React.useRef(0);
  const askEvent = React.useCallback(
    (eventId: string) => {
      const request = ++latestEvent.current;
      const askedAt = Date.now();
      const land = (load: Load<EventSnapshot | null>) => {
        if (latestEvent.current === request) setEventRead({ placeId, eventId, askedAt, load });
      };
      land({ state: "loading" });
      api.event(eventId).then(
        (data) => land({ state: "ready", data, sample: sampleMode, readAt: sampleMode ? null : Date.now(), refreshFailed: false }),
        (e: unknown) => {
          console.warn("Could not read the event to change:", e);
          land({ state: "error", message: errorMessage(e, "Could not read the event.") });
        },
      );
    },
    [api, placeId, sampleMode],
  );

  // After a lost change, cancel or remake, Next up is read again at once (so
  // no stale row is acted on meanwhile) and again as after a lost create
  // (CHECKS_AFTER_MS: a save can finish after its answer was lost, e.g.
  // behind a gateway timeout); a read for a place left behind is not made.
  const rereadLater = React.useCallback(() => {
    for (const delay of REREADS_AFTER_LOSS_MS) {
      after(delay, () => {
        if (placeNow.current === placeId) void onChanged();
      });
    }
  }, [placeId, onChanged, after]);
  // `isLostAnswer` tells a lost answer from a refusal.
  const guards = React.useMemo(() => ({ reread: rereadLater, isLost: isLostAnswer }), [rereadLater]);

  const change = React.useMemo(
    () =>
      singleFlight((before: EventSnapshot, input: UpdateEventInput) =>
        oneWrite(async () => {
          const event = await changeEvent(before, input, api, guards);
          setMade(keepIfUndoing);
          setChanged((prev) => keepIfUndoing(prev) ?? { placeId, event });
          void onChanged();
        }),
      ),
    [api, placeId, guards, onChanged, oneWrite],
  );

  const cancel = React.useMemo(
    () =>
      singleFlight((before: EventSnapshot) =>
        oneWrite(async () => {
          const event = await cancelComingEvent(before, api, guards);
          setMade(keepIfUndoing);
          setChanged((prev) => keepIfUndoing(prev) ?? { placeId, event });
          void onChanged();
        }),
      ),
    [api, placeId, guards, onChanged, oneWrite],
  );

  // Undo a change or a cancel, one write at a time (a cancel's undo makes an
  // event: a second press must never make a second). It always settles. A
  // cancel's undo failing in a way `undoChangedEvent` did not foresee may
  // still have made the event, so it is taken as lost (no Undo again).
  const undoChange = React.useCallback(() => {
    const now = Date.now();
    if (!changedHere || !canUndoChange(changedHere, now) || writes.busy) return;
    const target = changedHere;
    const set = (undo: ChangedEvent["undo"]) =>
      setChanged((prev) =>
        prev?.event.id === target.id && prev.event.madeAt === target.madeAt ? { ...prev, event: { ...prev.event, undo } } : prev,
      );
    set("undoing");
    oneWrite(() => undoChangedEvent(target, api, guards, now)).then(
      (undo) => {
        set(undo);
        if (typeof undo === "object" && "undone" in undo) void onChanged();
      },
      (e: unknown) => {
        console.warn("Could not undo:", e);
        if (target.kind === "cancelled") {
          set({ lost: remakeLostLine(target.title) });
          rereadLater();
        } else set({ failed: failureLine(e, "undo it") });
      },
    );
  }, [changedHere, api, guards, onChanged, oneWrite, writes, rereadLater]);

  return {
    options: mine,
    askOptions,
    username: user.username,
    made: madeHere,
    create,
    checking: checking?.placeId === placeId,
    undo,
    eventRead: eventRead?.placeId === placeId ? { eventId: eventRead.eventId, askedAt: eventRead.askedAt, load: eventRead.load } : null,
    askEvent,
    change,
    cancel,
    changed: changedHere,
    undoChange,
  };

}
